import { ApiErrorPayload, ApiErrorWithResponse } from './types';

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const isApiErrorPayload = (value: unknown): value is ApiErrorPayload => {
  if (!isRecord(value)) {
    return false;
  }

  return ['failureReasons', 'error', 'detail', 'message']
    .some(key => typeof value[key] === 'string');
};

/**
 * Diagnostic carriers, not a parsed body: "Request failed with status code 409", "x.map is not
 * a function". The flag is checked as well as `instanceof` to catch a second copy of axios.
 */
const isThrownError = (value: unknown): boolean => {
  return value instanceof Error || (isRecord(value) && value.isAxiosError === true);
};

const pickFirstMessage = (payload: ApiErrorPayload): string | null => {
  const message = [
    payload.failureReasons,
    payload.detail,
    payload.message,
    payload.error,
  ].find((value): value is string => typeof value === 'string' && value.trim().length > 0);

  return message ?? null;
};

export const getApiErrorMessage = (error: unknown, fallbackMessage: string = 'An error occurred'): string => {
  const candidate = error as ApiErrorWithResponse;
  const responseData = candidate?.response?.data;

  if (isApiErrorPayload(responseData)) {
    return pickFirstMessage(responseData) ?? fallbackMessage;
  }

  // Only a parsed JSON body is user-facing copy; everything else falls through to the fallback.
  if (!isThrownError(error) && isApiErrorPayload(error)) {
    return pickFirstMessage(error) ?? fallbackMessage;
  }

  return fallbackMessage;
};
